import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { updateConfig as globalUpdateConfig } from "../../../store/slices/Global";
import {
  user,
  useGetSplashScreenListDataQuery,
  useUpdateSplashScreenHeadDataMutation,
  useUpdateSplashScreenStatusMutation,
} from "../../../store/queries/User";
import { IconText } from "@wac-ui-dashboard/wac_component_library";
import { deleteSplashScreen } from "./api";
import { toast } from "react-toastify";
import { current } from "@reduxjs/toolkit";
import {
  getSplashScreenEditData,
  updateConfig,
} from "../../../store/slices/User/SplashScreen/splashScreenSlice";

const useSplashScrreen = () => {
  let paginationOptions = [
    { label: "10 Per page", value: 10 },
    { label: "20 Per page", value: 20 },
    { label: "30 Per page", value: 30 },
  ];
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoData, setVideoData] = useState("");
  const [imageData, setImageData] = useState("");
  // The row whose status toggle is waiting on the "common users" confirmation.
  const [pendingToggle, setPendingToggle] = useState(null);
  const dispatch = useDispatch();
  const splashScreenState = useSelector((state) => state.splash);
  const { selectedId } = useSelector((state) => state.splash);
  const { showEditModal } = useSelector((state) => state.global);
  const {
    data: splashScreenList = {},
    isLoading,
    isFetching,
    refetch,
  } = useGetSplashScreenListDataQuery({
    page_size: splashScreenState?.currentPageSize,
    page: splashScreenState?.currentPage,
    sort: splashScreenState?.sortBy,
    sort_order: splashScreenState?.sortOrder,
    search: splashScreenState?.search,
  });
  const [updateSplashScreenFields] = useUpdateSplashScreenHeadDataMutation();
  const [updateSplashScreenStatus] = useUpdateSplashScreenStatusMutation();
  const handleCreateClick = () => {
    dispatch(
      updateConfig((state) => {
        state.showCreateModal = !state.showCreateModal;
        state.isEdit = false;
        state.editSplashScreenData = {};
        state.videoPreview = "";
        state.arabicVideoPreview = "";
        state.videoName = "";
        state.arabicVideoName = "";
        state.isLoadingVideo = false;
        state.isArabicVideoLoading = false;
      })
    );
  };
  useEffect(() => {
    if (splashScreenState.clearSelection) {
      dispatch(
        updateConfig((state) => {
          state.clearSelection = false;
        })
      );
    }
    // eslint-disable-next-line
  }, [splashScreenState.clearSelection]);

  const handleSearch = (e) => {
    dispatch(
      updateConfig((state) => {
        state.search = e.target.value;
        state.currentPage = 1;
      })
    );
  };
  const handleClearClick = () => {
    dispatch(updateConfig((state) => (state.search = "")));
  };

  const handleEditColumnsClick = () => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = !state.showEditModal;
      })
    );
  };

  /**
   * Switching off the one all-users screen leaves customers who are on no number list with
   * no splash screen at all. Same warning the edit form gives - this is the other way in.
   */
  const handleToggle = (e, data) => {
    const newStatus = data.status === 1 ? 2 : 1;
    if (newStatus === 2 && data?.is_active_for_all_users) {
      setPendingToggle(data);
      return;
    }
    applyToggle(data);
  };

  const handleConfirmToggle = () => {
    const data = pendingToggle;
    setPendingToggle(null);
    if (data) {
      applyToggle(data);
    }
  };

  const handleCancelToggle = () => setPendingToggle(null);

  const applyToggle = (data) => {
    const newStatus = data.status === 1 ? 2 : 1;
    let body = {
      splash_screen_id: data?.id,
      status: newStatus,
    };
    updateSplashScreenStatus(body).then((response) => {
      if (response?.data?.status_code === 200) {
        dispatch(
          user.util.updateQueryData(
            "getSplashScreenListData",
            {
              page_size: splashScreenState?.currentPageSize,
              page: splashScreenState?.currentPage,
              sort: splashScreenState?.sortBy,
              sort_order: splashScreenState?.sortOrder,
              search: splashScreenState?.search,
            },
            (cacheEntry) => {
              let currentCache = { ...current(cacheEntry) };
              const newResult = currentCache.data.result?.map((res) =>
                res.id === data?.id
                  ? {
                      ...res,
                      status: newStatus,
                    }
                  : res
              );
              currentCache = {
                ...currentCache,
                data: {
                  ...currentCache.data,
                  result: newResult,
                },
              };
              return currentCache;
            }
          )
        );
      } else if (
        response?.data?.status_code === 422 &&
        response?.data?.errors
      ) {
        const firstErrorKey = Object.keys(response?.data?.errors)[0];
        const firstErrorMessage = response?.data?.errors[firstErrorKey];
        toast.error(firstErrorMessage ?? "Something went wrong!", {
          style: { minWidth: "320px" },
        });
      } else {
        toast.error(`Something went wrong!`);
      }
    });
  };

  const handleViewVideo = (data) => {
    setShowVideoModal(() => true);
    setVideoData(data);
  };

  const getRow = (field, data) => {
    const rows = {
      common: (field, data) => <p className="pro-mb-0">{data[field]}</p>,
      media_path: (field, data) => {
        return (
          <div className="no-wrap">
            <IconText
              icon={
                <span className="material-symbols-outlined x4">
                  play_circle
                </span>
              }
              title={`${"View Video"}`}
              onClick={() => handleViewVideo(data?.media_path)}
            />
          </div>
        );
      },
      alias_media_path: (field, data) => {
        return (
          <div className="no-wrap">
            <IconText
              icon={
                <span className="material-symbols-outlined x4">
                  play_circle
                </span>
              }
              title={`${"View Video"}`}
              onClick={() => handleViewVideo(data?.alias_media_path)}
            />
          </div>
        );
      },
      status: (field, data) => {
        const isChecked = data?.status === 1;
        return (
          <div className="form-check form-switch ">
            <input
              className="form-check-input cursor-pointer"
              type="checkbox"
              role="switch"
              id="flexSwitchCheckDefault"
              checked={isChecked}
              onChange={(e) => handleToggle(e, data)}
            />
          </div>
        );
      },
      button_color: (field, data) => (
        <div className="d-flex align-items-center color_code">
          <span
            style={{
              display: "inline-block",
              width: 20,
              height: 20,
              borderRadius: "50%",
              backgroundColor: data?.button_color,
              border: "1px solid #ccc",
            }}
          />
          <span>{data?.button_color}</span>
        </div>
      ),

      text_color: (field, data) => (
        <div className="d-flex align-items-center color_code">
          <span
            style={{
              display: "inline-block",
              width: 20,
              height: 20,
              borderRadius: "50%",
              backgroundColor: data?.text_color,
              border: "1px solid #ccc",
            }}
          />
          <span>{data?.text_color}</span>
        </div>
      ),
    };
    return rows?.[field]?.(field, data) ?? rows["common"](field, data);
  };

  const handleSort = (label) => {
    if (splashScreenState.sortBy === label) {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortOrder =
            splashScreenState.sortOrder === "asc" ? "desc" : "asc";
        })
      );
    } else {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortBy = label;
          state.sortOrder = "asc";
        })
      );
    }
  };

  const handleDeleteModal = (e) => {
    dispatch(
      updateConfig((state) => {
        state.showDeleteModal = !state.showDeleteModal;
        state.selectedId = e?.[0];
      })
    );
  };

  const handleDelete = async () => {
    const response = await deleteSplashScreen(selectedId);
    if (response && response.status === 200) {
      toast.success(`Deleted successfully.`);
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.search = "";
        })
      );
      setTimeout(() => {
        refetch();
      }, 0);
    } else {
      toast.error(`Something went wrong !`);
    }
    handleDeleteModal();
    dispatch(
      updateConfig((state) => {
        state.clearSelection = true;
      })
    );
  };

  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.currentPage = page;
      })
    );
  };
  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.currentPageSize = page_size;
        state.currentPage = 1;
      })
    );
  };
  const closeVideoModal = () => {
    setShowVideoModal(() => false);
    setVideoData("");
  };

  const handleEditAction = (e) => {
    dispatch(
      updateConfig((state) => {
        state.showCreateModal = true;
        state.selectedId = e?.[0];
        state.isEdit = true;
      })
    );
    dispatch(getSplashScreenEditData(e?.[0]));
  };

  return {
    isLoading,
    imageData,
    isFetching,
    splashScreenList,
    showEditModal,
    splashScreenState,
    paginationOptions,
    handleEditColumnsClick,
    updateSplashScreenFields,
    handleDeleteModal,
    handleCreateClick,
    handlePagination,
    handleClearClick,
    handleEditAction,
    closeVideoModal,
    handlePageSize,
    handleSearch,
    handleSort,
    handleDelete,
    refetch,
    getRow,
    showVideoModal,
    videoData,
    pendingToggle,
    handleConfirmToggle,
    handleCancelToggle,
  };
};

export default useSplashScrreen;
